// dataLayer 계약을 한곳에 모은다. 앱의 어느 컴포넌트도 직접 push 하지 않는다.
//
// 필드명은 전부 snake_case — GTM 에서 이름 변경 없이 GA4 파라미터로 1:1 매핑하기 위한 것.

import {
  EVENT_IDENTITY,
  EVENT_ORDER_BUTTON_CLICK,
  EVENT_PAGE_VIEW,
} from "@/shared/analytics/constants";
import { pushToDataLayer } from "@/shared/analytics/data-layer";
import {
  AccountClass,
  resolveAccountClass,
  resolveTrafficType,
  TrafficType,
} from "@/shared/analytics/traffic-type";
import {
  readStoredTrafficType,
  rememberTrafficType,
} from "@/shared/analytics/traffic-type-storage";
import type { Identity } from "@/features/ui/hooks/use-identity";

// 중복 발사 방지 상태를 useRef 가 아니라 모듈 스코프에 두는 이유:
//  - AppLayout 은 index.tsx 의 ErrorBoundary 안에 있어, 바운더리가 리셋되면 리마운트되고 ref 가 날아간다.
//  - next.config.production.js 는 reactStrictMode: true, 개발 설정은 false 라 두 설정이 어긋난다.
//  - 모듈 상태라야 useAnalytics() 가 멱등해지고, AppLayout 밖(404/500 트리)에서 한 번 더
//    호출해도 이중 발사가 없다.
let lastIdentityKey: string | undefined;
let lastPageViewPath: string | undefined;
let hasSeenInitialLocation = false;

export interface AnalyticsContext {
  trafficType: TrafficType;
  accountClass: AccountClass;
}

/**
 * 신원 스냅샷이 바뀌었을 때만 pathrade_identity 를 푸시하고,
 * 다음 방문의 첫 히트를 위해 traffic_type 을 남긴다. 항상 현재 컨텍스트를 돌려준다.
 *
 * user_id 등을 undefined 가 아니라 null 로 보내는 것이 중요하다 —
 * GTM 은 push 들을 영속 모델에 병합하므로 undefined 는 이전 값을 그대로 남긴다.
 */
export function syncAnalyticsIdentity(
  identity: Identity | null
): AnalyticsContext {
  const accountClass = resolveAccountClass(identity);
  const trafficType = identity
    ? resolveTrafficType(identity)
    : readStoredTrafficType() ?? "external";

  // 게스트에게는 쓰지 않는다 (첫 방문자 오라벨링 방지).
  if (identity) {
    rememberTrafficType(trafficType);
  }

  const key = `${identity?.id ?? 0}:${identity?.company?.id ?? 0}:${accountClass}`;
  if (key === lastIdentityKey) {
    return { trafficType, accountClass };
  }
  lastIdentityKey = key;

  pushToDataLayer({
    event: EVENT_IDENTITY,
    user_id: identity?.id ?? null,
    company_id: identity?.company?.id ?? null,
    company_type: identity?.company?.type ?? null,
    traffic_type: trafficType,
    account_class: accountClass,
  });

  return { trafficType, accountClass };
}

/**
 * SPA 라우트 변경당 1회.
 *
 * 최초 위치는 건너뛴다 — GTM 의 Google 태그가 컨테이너 로드 시 이미 page_view 를 보냈다.
 * 이 규칙이 "/" → "/ko" 로케일 리다이렉트의 이중 발사도 같이 해결한다.
 *
 * 중복 판정과 page_path 는 pathname 만 본다 (location.search 제외).
 * 이 앱은 필터·페이지네이션·탭 전환을 전부 쿼리 파라미터로 처리하므로, search 를 포함하면
 * 목록 화면 한 번 쓰는 동안 페이지뷰가 수십 건 찍힌다. 필터 사용 행태가 필요해지면
 * 페이지뷰를 오염시키지 않고 별도 커스텀 이벤트로 붙이는 것이 맞다.
 *
 * page_location / page_title 은 보내지 않는다 — GTM 내장 변수로 태그 발동 시점에 읽는다.
 * react-helmet-async 가 자식 이펙트에서 document.title 을 쓰기 때문에, 부모인 AppLayout
 * 이펙트에서 읽으면 이전 페이지 제목을 잡는다. 그래서 push 자체를 한 프레임 미룬다 —
 * 그동안 Helmet 이 제목을 확정하고, 같은 tick 의 리다이렉트 체인도 흘러간다.
 *
 * 건너뛰기·중복 판정은 동기로 처리하고 push 만 미룬다. 프레임 안에서 판정하면
 * 빠른 내비게이션으로 프레임이 취소될 때 "최초 위치" 플래그가 서지 않아,
 * 다음 페이지뷰가 대신 최초로 오인돼 버려진다.
 */
export function trackPageView(
  input: { path: string; locale: string } & AnalyticsContext
): void {
  if (!hasSeenInitialLocation) {
    hasSeenInitialLocation = true;
    lastPageViewPath = input.path;
    return;
  }
  if (input.path === lastPageViewPath) {
    return;
  }
  lastPageViewPath = input.path;

  const payload = {
    event: EVENT_PAGE_VIEW,
    page_path: input.path,
    page_locale: input.locale,
    traffic_type: input.trafficType,
    account_class: input.accountClass,
  };

  if (typeof window === "undefined") {
    pushToDataLayer(payload);
    return;
  }
  // 취소하지 않는다 — 이동이 확정된 페이지뷰이므로 언마운트 후에도 기록돼야 한다.
  window.requestAnimationFrame(() => pushToDataLayer(payload));
}

/**
 * 로그아웃. GA4 User-ID 를 비우고 게스트로 되돌린다.
 * traffic_type 은 유지한다 — 로그아웃한 내부 직원도 내부 트래픽이다.
 */
export function resetAnalyticsIdentity(): void {
  lastIdentityKey = undefined;

  pushToDataLayer({
    event: EVENT_IDENTITY,
    user_id: null,
    company_id: null,
    company_type: null,
    traffic_type: readStoredTrafficType() ?? "external",
    account_class: "guest",
  });
}

export function trackOrderButtonClick(input: {
  identity: Identity | null;
  orderSheetSetId?: number;
  orderSheetId?: number;
}): void {
  pushToDataLayer({
    event: EVENT_ORDER_BUTTON_CLICK,
    page_path: "order_sheet_tab",
    user_id: input.identity?.id ?? null,
    company_id: input.identity?.company?.id ?? null,
    company_type: input.identity?.company?.type ?? null,
    order_sheet_set_id: input.orderSheetSetId ?? null,
    order_sheet_id: input.orderSheetId ?? null,
    traffic_type: resolveTrafficType(input.identity),
    account_class: resolveAccountClass(input.identity),
  });
}
